import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText, Building2, Search } from "lucide-react";
import { bondTrusteesAPI, bondsAPI, formatDate } from "../../services/api";
import LoadingSpinner from "../common/LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";

const STATUS_CONFIG = {
    Draft: { className: "bg-gray-100 text-gray-800", icon: "📝" },
    "Pending L1": { className: "bg-yellow-100 text-yellow-800", icon: "①" },
    "Pending L2": { className: "bg-yellow-100 text-yellow-800", icon: "②" },
    "Pending Review": { className: "bg-yellow-100 text-yellow-800", icon: "⏳" },
    "Awaiting Payment Reference": { className: "bg-orange-100 text-orange-800", icon: "💳" },
    Approved: { className: "bg-green-100 text-green-800", icon: "✅" },
    Rejected: { className: "bg-red-100 text-red-800", icon: "❌" },
    Listed: { className: "bg-green-100 text-green-800", icon: "🏛️" },
    Active: { className: "bg-green-100 text-green-800", icon: "✅" },
    Matured: { className: "bg-blue-100 text-blue-800", icon: "🏁" },
};

const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || {
        className: "bg-gray-100 text-gray-800",
        icon: "",
    };
    return (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.className}`}>
            {config.icon && <span className="mr-1">{config.icon}</span>}
            {status}
        </span>
    );
};

const DocumentManagement = ({ role = "issuer" }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 10;

    // Role-specific configurations
    const getRoleConfig = () => {
        const configs = {
            issuer: {
                basePath: "/issuer",
                queryKey: "issuer-bonds",
                subtitle: "Manage your bond-related documents and reports"
            },
            "facility-agent": {
                basePath: "/facility-agent",
                queryKey: "fa-bonds",
                subtitle: "View bond and subscription documents and reports"
            },
            trustee: {
                basePath: "/trustee",
                queryKey: "trustee-bonds",
                subtitle: "Manage trustee documents and bond-related reports"
            },
            admin: {
                basePath: "/admin",
                queryKey: "admin-bonds",
                subtitle: "Manage bond documents and reports"
            }
        };
        return configs[role] || configs.issuer;
    };

    const config = getRoleConfig();

    // Debounce search term
    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Reset to first page when status filter changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter]);

    // Fetch bonds - use different API for trustees
    const { data: bondsResponse, isPending: bondsLoading } = useQuery({
        queryKey: [config.queryKey, user?.id, debouncedSearchTerm, statusFilter, currentPage],
        queryFn: async () => {
            if (role === "trustee") {
                // For trustees, fetch assigned bonds
                return bondTrusteesAPI.getTrusteeBonds(user.id || user._id);
            } else {
                // For other roles, use standard bonds API
                return bondsAPI.getBonds({
                    search: debouncedSearchTerm || undefined,
                    status: statusFilter === "all" ? undefined : statusFilter,
                    page: currentPage,
                    limit: PAGE_SIZE,
                });
            }
        },
        enabled: role !== "trustee" || !!user,
    });

    // Derive bonds and totals from axios response safely
    let bonds = [];
    let totalBonds = 0;

    if (role === "trustee") {
        // Handle trustee bond assignments
        const assignments = bondsResponse?.data?.data || bondsResponse?.data || [];
        const assignmentsArray = Array.isArray(assignments) ? assignments : [];
        const allBonds = assignmentsArray.map(assignment => assignment.bondId).filter(Boolean);

        // Apply search filter
        const searchFiltered = debouncedSearchTerm
            ? allBonds.filter(bond =>
                bond.bondName?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
            : allBonds;

        // Apply status filter
        const statusFiltered = statusFilter === "all"
            ? searchFiltered
            : searchFiltered.filter(bond => bond.status === statusFilter);

        totalBonds = statusFiltered.length;

        // Apply pagination
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        bonds = statusFiltered.slice(startIndex, startIndex + PAGE_SIZE);
    } else {
        // Handle other roles
        const respRoot = bondsResponse?.data?.data ?? bondsResponse?.data ?? bondsResponse;
        bonds = Array.isArray(respRoot?.bonds)
            ? respRoot.bonds
            : Array.isArray(respRoot?.data)
                ? respRoot.data
                : Array.isArray(respRoot?.items)
                    ? respRoot.items
                    : Array.isArray(respRoot?.docs)
                        ? respRoot.docs
                        : Array.isArray(respRoot?.results)
                            ? respRoot.results
                            : Array.isArray(respRoot)
                                ? respRoot
                                : [];

        totalBonds = typeof respRoot?.total === 'number'
            ? respRoot.total
            : typeof respRoot?.count === 'number'
                ? respRoot.count
                : typeof respRoot?.pagination?.total === 'number'
                    ? respRoot.pagination.total
                    : bonds.length;
    }

    const totalPages = Math.max(1, Math.ceil((totalBonds || bonds.length) / PAGE_SIZE));

    return (
        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="mb-4">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">Bond Documents</h2>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-4 mb-6 sm:flex-row">
                <div className="relative flex-1">
                    <Search className="absolute w-5 h-5 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                    <input
                        type="text"
                        placeholder="Search bonds..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 pr-8 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 min-w-[140px] whitespace-nowrap bg-white"
                >
                    <option value="all">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Draft">Draft</option>
                    <option value="Rejected">Rejected</option>
                </select>
            </div>

            {/* Bonds Table */}
            {bondsLoading ? (
                <LoadingSpinner size="lg" text="Loading bonds..." />
            ) : (
                <div className="overflow-hidden bg-white rounded-lg shadow">
                    {bonds.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p>No bonds found</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                            Bond Name
                                        </th>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                            Issue Date
                                        </th>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                            Maturity Date
                                        </th>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {bonds.map((bond) => (
                                        <tr key={bond._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {bond.bondName}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">
                                                    {bond.issueDate ? formatDate(bond.issueDate) : "N/A"}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">
                                                    {bond.maturityDate ? formatDate(bond.maturityDate) : "N/A"}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(bond.status)}
                                            </td>
                                            <td className="px-6 py-4 text-sm whitespace-nowrap">
                                                <button
                                                    onClick={() => navigate(`${config.basePath}/bonds/${bond._id}/documents`)}
                                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-900"
                                                    title="View Documents"
                                                >
                                                    <FileText className="w-4 h-4" />
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {bonds.length > 0 && (
                        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                            <div className="flex justify-between flex-1 sm:hidden">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Showing {' '}
                                        <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>
                                        {' '} - {' '}
                                        <span className="font-medium">
                                            {Math.min(currentPage * PAGE_SIZE, totalBonds)}
                                        </span>
                                        {' '} of {' '}
                                        <span className="font-medium">{totalBonds}</span>
                                        {' '} results
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span className="sr-only">Previous</span>
                                            <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                        {[...Array(totalPages)].map((_, idx) => {
                                            const pageNum = idx + 1;
                                            const showPage = pageNum === 1 ||
                                                pageNum === totalPages ||
                                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);

                                            if (!showPage) {
                                                if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                                    return (
                                                        <span
                                                            key={pageNum}
                                                            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300"
                                                        >
                                                            ...
                                                        </span>
                                                    );
                                                }
                                                return null;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span className="sr-only">Next</span>
                                            <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DocumentManagement;
